import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Check, Mail, Search, UserPlus, X } from "lucide-react";
import { Avatar, AvatarFallback } from "@v2/components/ui/avatar";
import { Button } from "@v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@v2/components/ui/tabs";
import { Textarea } from "@v2/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { PLAYER_ROLES, playerRoleLabel } from "../../lib/labels";
import { usePermissions } from "../../lib/permissions";
import { useWorkspaceData } from "../../lib/workspace-data";
import { copyToClipboard } from "../../lib/share";
import { EmptyState, SectionLoading } from "../../components/state";
import type { Player, TryoutStatus } from "../../types";

export function GuestsView({
  teamId,
  teamName,
  onOpenPlayer,
}: {
  teamId: string;
  teamName: string;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("guests");
  const TAB_LABELS: Record<TryoutStatus, string> = {
    evaluating: t("tabs.evaluating"),
    promoted: t("tabs.promoted"),
    rejected: t("tabs.rejected"),
  };
  const queryClient = useQueryClient();
  const { canManage } = usePermissions();
  const { players, responses, isLoading } = useWorkspaceData(teamId);
  const [tab, setTab] = useState<TryoutStatus>("evaluating");
  const [searchTerm, setSearchTerm] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createRoleCode, setCreateRoleCode] = useState<string>("");
  const [promoteTarget, setPromoteTarget] = useState<Player | null>(null);
  const [inviteTarget, setInviteTarget] = useState<Player | null>(null);

  // Includiamo anche i player promossi (is_guest=false ma tryout_status='promoted')
  // per mantenere lo storico delle promozioni nel tab "Promossi".
  const guests = useMemo(
    () => players.filter((p) => p.is_guest || p.tryout_status === "promoted"),
    [players],
  );

  const counts = useMemo(() => {
    const acc = { evaluating: 0, promoted: 0, rejected: 0 };
    for (const g of guests) {
      const status = (g.tryout_status as TryoutStatus | undefined) || "evaluating";
      acc[status] = (acc[status] || 0) + 1;
    }
    return acc;
  }, [guests]);

  const stats = useMemo(() => {
    const map = new Map<string, { sessions: number; presences: number }>();
    for (const g of guests) map.set(g.id, { sessions: 0, presences: 0 });
    for (const r of responses) {
      const entry = map.get(r.player_id);
      if (!entry) continue;
      if (r.status !== "no_response") entry.sessions += 1;
      if (r.status === "present" || r.status === "late") entry.presences += 1;
    }
    return map;
  }, [guests, responses]);

  const labelFor = (g: Player): string =>
    (g.display_name && g.display_name.trim()) || g.ea_account_handle || "";

  const visible = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return guests
      .filter((g) => ((g.tryout_status as TryoutStatus | undefined) || "evaluating") === tab)
      .filter((g) => {
        if (!needle) return true;
        const hay = `${labelFor(g)} ${g.ea_account_handle || ""} ${g.role_code || ""}`.toLowerCase();
        return hay.includes(needle);
      })
      .sort((a, b) => labelFor(a).localeCompare(labelFor(b)));
  }, [guests, tab, searchTerm]);

  const createGuest = useMutation({
    mutationFn: async (formData: FormData) => {
      const firstName = String(formData.get("first_name") || "").trim();
      const lastName = String(formData.get("last_name") || "").trim();
      const eaHandle = String(formData.get("ea_account_handle") || "").trim();
      // Regola: serve almeno UNO tra nome, cognome o id EA - il founder
      // sceglie quale identificare con questo provinante.
      if (!firstName && !lastName && !eaHandle) {
        throw new Error(t("newDialog.identifierRequired"));
      }
      const payload = {
        team_id: teamId,
        first_name: firstName,
        last_name: lastName,
        role_code: createRoleCode || null,
        email: String(formData.get("email") || "").trim() || null,
        phone: String(formData.get("phone") || "").trim() || null,
        ea_account_handle: eaHandle || null,
        tryout_notes: String(formData.get("tryout_notes") || "").trim() || null,
        is_guest: true,
        tryout_status: "evaluating" as TryoutStatus,
      };
      const { error } = await supabaseV2.from("players").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      setCreateOpen(false);
      setCreateRoleCode("");
    },
    onError: (e: any) => window.alert(e.message || String(e)),
  });

  // Rifiuto provinante: la RPC setta tryout_status='rejected', status='inactive'
  // e fa uscire l'utente collegato da questa squadra (membership 'suspended').
  const rejectGuest = useMutation({
    mutationFn: async (playerId: string) => {
      const { error } = await supabaseV2.rpc("reject_guest_player", { _player_id: playerId });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["players", teamId] }),
    onError: (e: any) => window.alert(e.message || String(e)),
  });

  // Reinserimento in valutazione: inverso di reject (ripristina status e membership).
  const reinstateGuest = useMutation({
    mutationFn: async (playerId: string) => {
      const { error } = await supabaseV2.rpc("reinstate_guest_player", { _player_id: playerId });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["players", teamId] }),
    onError: (e: any) => window.alert(e.message || String(e)),
  });

  const promoteGuest = useMutation({
    mutationFn: async ({
      playerId,
      shirtNumber,
      roleCode,
    }: {
      playerId: string;
      shirtNumber: number | null;
      roleCode: string | null;
    }) => {
      const { error } = await supabaseV2.rpc("promote_guest_to_roster", {
        _player_id: playerId,
        _shirt_number: shirtNumber,
        _role_code: roleCode,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      setPromoteTarget(null);
    },
    onError: (e: any) => window.alert(e.message || String(e)),
  });

  return (
    <div className="space-y-5 animate-fade-in">
      <section className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <h2 className="text-2xl font-semibold">{t("title")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("summary", {
              evaluating: counts.evaluating,
              promoted: counts.promoted,
              rejected: counts.rejected,
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 sm:flex-none sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("search")}
              className="pl-9"
            />
          </div>
          {canManage && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <Button onClick={() => setCreateOpen(true)} variant="default">
                <UserPlus />
                {t("newGuest")}
              </Button>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("newDialog.title")}</DialogTitle>
                  <DialogDescription>
                    {t("newDialog.desc")}
                  </DialogDescription>
                </DialogHeader>
                <form
                  className="space-y-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    createGuest.mutate(new FormData(event.currentTarget));
                  }}
                >
                  <p className="text-[11px] text-muted-foreground">
                    {t("newDialog.identifierHint")}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>{t("newDialog.firstName")}</Label>
                      <Input name="first_name" autoComplete="off" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("newDialog.lastName")}</Label>
                      <Input name="last_name" autoComplete="off" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("newDialog.eaHandle")}</Label>
                    <Input name="ea_account_handle" autoComplete="off" placeholder={t("newDialog.eaHandlePh")} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5 col-span-1">
                      <Label>{t("newDialog.role")}</Label>
                      <Select
                        value={createRoleCode || "__none"}
                        onValueChange={(v) => setCreateRoleCode(v === "__none" ? "" : v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={t("newDialog.selectPh")} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">{t("newDialog.roleNone")}</SelectItem>
                          {PLAYER_ROLES.map((r) => (
                            <SelectItem key={r} value={r}>
                              <span className="font-mono mr-2">{r}</span>
                              <span className="text-muted-foreground">{playerRoleLabel(r)}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5 col-span-1">
                      <Label>{t("newDialog.phone")}</Label>
                      <Input name="phone" type="tel" />
                    </div>
                    <div className="space-y-1.5 col-span-1">
                      <Label>{t("newDialog.email")}</Label>
                      <Input name="email" type="email" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("newDialog.notes")}</Label>
                    <Textarea
                      name="tryout_notes"
                      rows={3}
                      placeholder={t("newDialog.notesPh")}
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                      {t("newDialog.cancel")}
                    </Button>
                    <Button type="submit" variant="default" disabled={createGuest.isPending}>
                      {createGuest.isPending ? t("newDialog.saving") : t("newDialog.create")}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </section>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TryoutStatus)}>
        <TabsList>
          <TabsTrigger value="evaluating">
            {TAB_LABELS.evaluating}
            <span className="ml-1.5 text-[10px] tabular-nums opacity-70">{counts.evaluating}</span>
          </TabsTrigger>
          <TabsTrigger value="promoted">
            {TAB_LABELS.promoted}
            <span className="ml-1.5 text-[10px] tabular-nums opacity-70">{counts.promoted}</span>
          </TabsTrigger>
          <TabsTrigger value="rejected">
            {TAB_LABELS.rejected}
            <span className="ml-1.5 text-[10px] tabular-nums opacity-70">{counts.rejected}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <SectionLoading />
      ) : visible.length === 0 ? (
        <EmptyState
          text={
            tab === "evaluating"
              ? t("empty.evaluating")
              : tab === "promoted"
              ? t("empty.promoted")
              : t("empty.rejected")
          }
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {visible.map((guest) => {
            const s = stats.get(guest.id) || { sessions: 0, presences: 0 };
            return (
              <GuestCard
                key={guest.id}
                guest={guest}
                tab={tab}
                sessions={s.sessions}
                presences={s.presences}
                canManage={canManage}
                onOpen={() => onOpenPlayer(guest.id)}
                onPromote={() => setPromoteTarget(guest)}
                onReject={() => {
                  const label = labelFor(guest) || guest.id.slice(0, 8);
                  if (window.confirm(t("card.rejectConfirm", { name: label }))) {
                    rejectGuest.mutate(guest.id);
                  }
                }}
                onReinstate={() => reinstateGuest.mutate(guest.id)}
                onInvite={() => setInviteTarget(guest)}
                isSaving={rejectGuest.isPending || reinstateGuest.isPending}
              />
            );
          })}
        </div>
      )}

      {promoteTarget && (
        <PromoteDialog
          guest={promoteTarget}
          isSaving={promoteGuest.isPending}
          onCancel={() => setPromoteTarget(null)}
          onConfirm={(shirtNumber, roleCode) =>
            promoteGuest.mutate({ playerId: promoteTarget.id, shirtNumber, roleCode })
          }
        />
      )}

      {inviteTarget && (
        <InviteGuestDialog
          guest={inviteTarget}
          teamName={teamName}
          onClose={() => setInviteTarget(null)}
        />
      )}
    </div>
  );
}

function GuestCard({
  guest,
  tab,
  sessions,
  presences,
  canManage,
  onOpen,
  onPromote,
  onReject,
  onReinstate,
  onInvite,
  isSaving,
}: {
  guest: Player;
  tab: TryoutStatus;
  sessions: number;
  presences: number;
  canManage: boolean;
  onOpen: () => void;
  onPromote: () => void;
  onReject: () => void;
  onReinstate: () => void;
  onInvite: () => void;
  isSaving: boolean;
}) {
  const { t } = useTranslation("guests");
  const presenceRate = sessions > 0 ? Math.round((presences / sessions) * 100) : null;
  // Fallback display: se display_name è vuoto (es. provinante creato solo
  // con ea_handle), usa il gamertag EA, altrimenti un trattino.
  const displayLabel =
    (guest.display_name && guest.display_name.trim()) || guest.ea_account_handle || "-";
  return (
    <div className="rounded-[20px] bg-card p-4 flex flex-col gap-3">
      <button onClick={onOpen} className="text-left flex items-center gap-3 group min-w-0">
        <Avatar className="h-12 w-12 shrink-0">
          <AvatarFallback className="bg-raised text-foreground font-semibold">
            {displayLabel.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            <p className="font-semibold truncate group-hover:underline underline-offset-2">
              {displayLabel}
            </p>
            {tab === "promoted" && guest.shirt_number != null && (
              <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                #{guest.shirt_number}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {guest.role_code || t("card.roleNa")}
            {guest.phone ? ` · ${guest.phone}` : ""}
          </p>
        </div>
      </button>
      <div className="grid grid-cols-3 gap-1.5 text-center">
        <Stat label={t("card.calledUp")} value={sessions} />
        <Stat label={t("card.present")} value={presences} accent="text-[hsl(var(--ok))]" />
        <Stat label={t("card.rate")} value={presenceRate !== null ? `${presenceRate}%` : "-"} />
      </div>
      {guest.tryout_notes && (
        <p className="text-[11px] text-muted-foreground line-clamp-2 leading-snug">{guest.tryout_notes}</p>
      )}
      {canManage && tab === "evaluating" && (
        <>
          {guest.email && (
            <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
              <span className="truncate">
                {guest.user_id ? t("card.linkedAccount") : guest.email}
              </span>
              {!guest.user_id && (
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onInvite}>
                  <Mail />
                  {t("card.invite")}
                </Button>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" variant="default" className="flex-1" onClick={onPromote} disabled={isSaving}>
              <Check />
              {t("card.promote")}
            </Button>
            <Button size="sm" variant="outline" className="flex-1" onClick={onReject} disabled={isSaving}>
              <X />
              {t("card.archive")}
            </Button>
          </div>
        </>
      )}
      {canManage && tab === "rejected" && (
        <Button size="sm" variant="outline" onClick={onReinstate} disabled={isSaving}>
          {t("card.reinstate")}
        </Button>
      )}
    </div>
  );
}

function InviteGuestDialog({
  guest,
  teamName,
  onClose,
}: {
  guest: Player;
  teamName: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("guests");
  const email = guest.email || "";
  const signupUrl = `${window.location.origin}/signup?email=${encodeURIComponent(email)}`;
  const subject = t("inviteDialog.subject", { teamName });
  const body = [
    t("inviteDialog.greeting", { name: guest.first_name || guest.display_name }),
    "",
    t("inviteDialog.intro", { teamName }),
    "",
    t("inviteDialog.instructions"),
    signupUrl,
    "",
    t("inviteDialog.outro"),
    "",
    t("inviteDialog.signoff"),
  ].join("\n");
  const fullText = `${t("inviteDialog.subjectLine", { subject })}\n\n${body}`;
  const mailto = `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const [copied, setCopied] = useState(false);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("inviteDialog.title", { name: guest.display_name })}</DialogTitle>
          <DialogDescription>
            {t("inviteDialog.desc")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-[11px]">{t("inviteDialog.to")}</Label>
            <Input value={email} readOnly className="h-8 text-sm" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px]">{t("inviteDialog.preview")}</Label>
            <Textarea value={fullText} readOnly rows={10} className="text-xs font-mono" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            {t("inviteDialog.close")}
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              const ok = await copyToClipboard(fullText);
              if (ok) {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 2000);
              }
            }}
          >
            {copied ? t("inviteDialog.copied") : t("inviteDialog.copy")}
          </Button>
          <Button
            variant="default"
            onClick={() => {
              window.location.href = mailto;
            }}
          >
            <Mail />
            {t("inviteDialog.openMail")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className="rounded-lg bg-muted/50 px-1.5 py-1.5">
      <div className={`text-sm font-bold tabular-nums leading-tight ${accent || ""}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}

function PromoteDialog({
  guest,
  isSaving,
  onCancel,
  onConfirm,
}: {
  guest: Player;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: (shirtNumber: number | null, roleCode: string | null) => void;
}) {
  const { t } = useTranslation("guests");
  const [shirtNumber, setShirtNumber] = useState<string>("");
  const [roleCode, setRoleCode] = useState<string>(guest.role_code || "");
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("promoteDialog.title", { name: guest.display_name })}</DialogTitle>
          <DialogDescription>
            {t("promoteDialog.desc")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("promoteDialog.number")}</Label>
              <Input
                value={shirtNumber}
                onChange={(e) => setShirtNumber(e.target.value.replace(/[^0-9]/g, ""))}
                placeholder="-"
                inputMode="numeric"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("promoteDialog.role")}</Label>
              <Select value={roleCode || "__none"} onValueChange={(v) => setRoleCode(v === "__none" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder={t("promoteDialog.selectPh")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">{t("promoteDialog.roleNone")}</SelectItem>
                  {PLAYER_ROLES.map((r) => (
                    <SelectItem key={r} value={r}>
                      <span className="font-mono mr-2">{r}</span>
                      <span className="text-muted-foreground">{playerRoleLabel(r)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {t("promoteDialog.hint")}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            {t("promoteDialog.cancel")}
          </Button>
          <Button
            variant="default"
            onClick={() => {
              const sn = shirtNumber.trim() ? Number(shirtNumber) : null;
              const rc = roleCode.trim() || null;
              onConfirm(sn, rc);
            }}
            disabled={isSaving}
          >
            {isSaving ? t("promoteDialog.saving") : t("promoteDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
